import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import {
    DEFAULT_TABLE_ROW_OPTIONS,
    DEFAULT_TABLE_ROWS_PER_PAGE
} from '../../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../../utils/sortFunctions';

import SubmissionsTableRow from './SubmissionsTableRow';

const headCells = [
    { id: 'ptCode', numeric: false, label: 'ID' },
    { id: 'ptName', numeric: false, label: 'Participant Name' },
    { id: 'filename', numeric: false, label: 'CV' },
    { id: 'downloadCV', numeric: false, label: 'Download CV' },
    { id: 'status', numeric: false, label: 'Status' }
];

// const SubmissionsTable = ({ rows, roles, searchTerm, onUpdateParticipantDetail }) => {
const SubmissionsTable = ({
    rows,
    roles,
    searchTerm,
    onUpdateParticipantStatus,
    onSubmissionsRejectEditor,
    onSubmissionsRejectedRow,
    rowCancelled
}) => {
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('ptCode');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_TABLE_ROWS_PER_PAGE);

    // USE EFFECTS
    useEffect(() => {
        setOrder('asc');
        setOrderBy('ptCode');
    }, []);

    useEffect(() => {
        setPage(0);
    }, [searchTerm]);

    // HELPER FNS
    const onSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0);
    };

    const onChangePage = (e, newPage) => {
        const page = newPage > 0 ? newPage : 0;
        setPage(page);
    };

    const onChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    // RENDER
    return (
        <Box sx={{ width: '100%' }}>
            <Paper sx={{ width: '100%', mb: 2 }}>
                <TableContainer>
                    <Table sx={{ minWidth: 640 }} aria-labelledby="tableTitle" size="medium">
                        <TableHead>
                            <TableRow>
                                {headCells.map((headCell) => (
                                    <TableCell
                                        key={headCell.id}
                                        sortDirection={orderBy === headCell.id ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                            onClick={() => onSort(headCell.id)}>
                                            <Typography variant={'subtitle3'} color={'primary'}>
                                                {headCell.label}
                                            </Typography>
                                            {orderBy === headCell.id ? (
                                                <Box component="span" sx={visuallyHidden}>
                                                    {order === 'desc'
                                                        ? 'sorted descending'
                                                        : 'sorted ascending'}
                                                </Box>
                                            ) : null}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {stableSort(rows, orderBy, order)
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => (
                                    <SubmissionsTableRow
                                        key={row.id}
                                        row={row}
                                        roles={roles}
                                        onUpdateParticipantStatus={onUpdateParticipantStatus}
                                        onSubmissionsRejectEditor={onSubmissionsRejectEditor}
                                        onSubmissionsRejectedRow={onSubmissionsRejectedRow}
                                        rowCancelled={rowCancelled}
                                    />
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    data-testid="tablePagination"
                    rowsPerPageOptions={DEFAULT_TABLE_ROW_OPTIONS}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onChangePage}
                    onRowsPerPageChange={onChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

SubmissionsTable.propTypes = {
    rows: PropTypes.array.isRequired,
    roles: PropTypes.array,
    searchTerm: PropTypes.string,
    onUpdateParticipantStatus: PropTypes.func,
    onSubmissionsRejectEditor: PropTypes.func,
    // onPoolApplicationRejectCancelled: PropTypes.func,
    onSubmissionsRejectedRow: PropTypes.func,
    rowCancelled: PropTypes.bool
};

export default SubmissionsTable;
